import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { adminGuard, emailVerifiedGuard, kycFormGuard } from 'auth';
import { AppointmentsPageComponent, UserTreePageComponent } from 'credo';
import { UsersPageComponent } from './admin/containers/users-page/users-page.component';
import { XshopAdminPageComponent } from './admin/containers/xshop-admin-page/xshop-admin-page.component';
import { BetaActivationPageComponent } from './containers/pages/beta-activation-page/beta-activation-page.component';
import { BookingsPageComponent } from './containers/pages/bookings-page/bookings-page.component';
import { DrFuturePageComponent } from './containers/pages/dr-future-page/dr-future-page.component';
import { DNA_ASSISTANTS } from './services/dr-future-assistants';
import { DeleteAccountPageComponent } from './containers/pages/delete-account-page/delete-account-page.component';
import { FamilyJoinPageComponent } from './containers/pages/family-join-page/family-join-page.component';
import { FamilyPageComponent } from './containers/pages/family-page/family-page.component';
import { CompanionPageComponent } from './containers/pages/companion-page/companion-page.component';
import { CompanionGuardianPageComponent } from './containers/pages/companion-guardian-page/companion-guardian-page.component';
import { EarningsStatisticsPageComponent } from './containers/pages/earnings-statistics-page/earnings-statistics-page.component';
import { FunnelDirectCheckoutPageComponent } from './containers/pages/funnel-direct-checkout-page/funnel-direct-checkout-page.component';
import { FunnelCheckEmailPageComponent } from './containers/pages/funnel-check-email-page/funnel-check-email-page.component';
import { FunnelJoinPageComponent } from './containers/pages/funnel-join-page/funnel-join-page.component';
import { FunnelJoinStartPageComponent } from './containers/pages/funnel-join-start-page/funnel-join-start-page.component';
import { FunnelLandingPageComponent } from './containers/pages/funnel-landing-page/funnel-landing-page.component';
import { FunnelQuizPageComponent } from './containers/pages/funnel-quiz-page/funnel-quiz-page.component';
import { FunnelQuizStartPageComponent } from './containers/pages/funnel-quiz-start-page/funnel-quiz-start-page.component';
import { FunnelResultPageComponent } from './containers/pages/funnel-result-page/funnel-result-page.component';
import { HealthToolsPageComponent } from './containers/pages/health-tools-page/health-tools-page.component';
import { KycConfirmedPageComponent } from './containers/pages/kyc-confirmed-page/kyc-confirmed-page.component';
import { KycFormPageComponent } from './containers/pages/kyc-form-page/kyc-form-page.component';
import { KycSumsubPageComponent } from './containers/pages/kyc-sumsub-page/kyc-sumsub-page.component';
import { KycTokenPageComponent } from './containers/pages/kyc-token-page/kyc-token-page.component';
import { MainPageComponent } from './containers/pages/main-page/main-page.component';
import { MarketingPoolPageComponent } from './containers/pages/marketing-pool-page/marketing-pool-page.component';
import { marketingPoolAdminGuard, marketingPoolGuard } from './services/marketing-pool-access';
import { SupportHubPageComponent } from './containers/pages/support-hub-page/support-hub-page.component';
import { MembershipApplyPageComponent } from './containers/pages/membership-apply-page/membership-apply-page.component';
import { UserPayoutRequestPageComponent } from './containers/pages/user-payout-request-page/user-payout-request-page.component';
import { UserPayoutsPageComponent } from './containers/pages/user-payouts-page/user-payouts-page.component';
import { UserStatisticsPageComponent } from './containers/pages/user-statistics-page/user-statistics-page.component';

import { routeBuilder } from 'credo';

import { environment } from './environments/environment';

// Which funnel /r serves. 'membership' (default) is the direct purchase (customer
// decision 2026-10-02: no registration before the checkout — product, duration,
// name + e-mail + consents, payment); 'quiz' brings back the retired Health Finder landing +
// quiz, which is why those components and their routes are still here. Keep this
// in step with the backend's GLOBALHEALTH_FUNNEL_MODE: the backend decides where
// the magic-link mail points and refuses to open a quiz session in membership
// mode, so a half-flipped switch dead-ends the visitor.
const QUIZ_FUNNEL = environment.globalHealthFunnelMode === 'quiz';
const funnelLandingComponent = QUIZ_FUNNEL ? FunnelLandingPageComponent : FunnelDirectCheckoutPageComponent;

export const routes = routeBuilder()
  .main(MainPageComponent)
  .withMaintenance()
  .withLegalAcknowledgement()
  .withCommunity()
  // Global Health is config domain 5 and publishes its policy documents as
  // terms / privacy / imprint (see data/media/5/en/policy/). The login page's
  // built-in fallback asks for the fac3less names (terms_of_service /
  // privacy_policy / legal_notice), which do not exist here — every link came
  // back 500 ActionView::MissingTemplate.
  // "Register" on the login page leads into the membership funnel, not the plain
  // account form: Global Health has no sign-up outside the funnel.
  .withRegisterUrl('/r')
  .withLoginActions([
    { labelKey: 'policy.privacy',     policy: 'privacy' },
    { labelKey: 'policy.tos',         policy: 'terms' },
    { labelKey: 'policy.legalNotice', policy: 'imprint' }
  ])
  .root(
    {
      path: 'enter-b-e-t-a-test',
      redirectTo: () => {
        const lang = localStorage.getItem('language') || 'en';
        return `/${lang}/enter-b-e-t-a-test`;
      }
    },
    // FAC3LESS funnel — short marketing URLs (no :lang prefix) redirect into the lang-scoped page.
    // The trailing token/id is preserved by closing over the URL via the redirectTo callback.
    //
    // Both return a UrlTree, not a string: an absolute STRING redirect rebuilds the
    // query from the redirect target only (Angular's ApplyRedirects.createQueryParams),
    // so `/r/123?plan=weekly&weeks=12` would arrive as `/en/r/123` and the Website V2
    // hand-over (plan, weeks, journey, goal, ref — WEBSITE_V2_PLAN.md §3.3) would be
    // lost. The redirect function runs in an injection context, so inject() works.
    {
      path: 'r',
      // pathMatch: 'full' so this only matches the bare /r — without it Angular's
      // default 'prefix' match swallows /r/:sponsorId too and drops the sponsor id.
      pathMatch: 'full',
      redirectTo: ({ queryParams, fragment }) => {
        const lang = localStorage.getItem('language') || 'en';
        return inject(Router).createUrlTree(['/', lang, 'r'], { queryParams, fragment: fragment ?? undefined });
      }
    },
    {
      path: 'r/:sponsorId',
      redirectTo: ({ params, queryParams, fragment }) => {
        const lang = localStorage.getItem('language') || 'en';
        return inject(Router).createUrlTree(['/', lang, 'r', params['sponsorId']],
                                            { queryParams, fragment: fragment ?? undefined });
      }
    },
    // DNA Family SPECIAL invitation link without a language prefix (same UrlTree
    // reasoning as /r: the query must survive the redirect).
    {
      path: 'family/join/:token',
      redirectTo: ({ params, queryParams, fragment }) => {
        const lang = localStorage.getItem('language') || 'en';
        return inject(Router).createUrlTree(['/', lang, 'family', 'join', params['token']],
                                            { queryParams, fragment: fragment ?? undefined });
      }
    }
  )
  .lang.public(
    { path: 'kyc/confirm/:token', component: KycTokenPageComponent, data: { action: 'confirm', animation: 'kyc-confirm' } },
    { path: 'kyc/correct/:token', component: KycTokenPageComponent, data: { action: 'correct', animation: 'kyc-correct' } },
    // Funnel pages (public — no auth required). /r serves whichever funnel is
    // live; both share the check-email step and the same lead-capture backend.
    { path: 'r',                 component: funnelLandingComponent,        data: { animation: 'funnel-landing' } },
    { path: 'r/:sponsorId',      component: funnelLandingComponent,        data: { animation: 'funnel-landing' } },
    { path: 'quiz/check-email',  component: FunnelCheckEmailPageComponent, data: { animation: 'funnel-check-email' } },
    // The five marketing screens + magic-link signup that /r served until the
    // direct checkout (2026-10-02). Not linked from anywhere any more; kept routed
    // so the old lead flow can be reached and brought back without code changes.
    { path: 'join',              component: FunnelJoinPageComponent,       data: { animation: 'funnel-join' } },
    // Magic-link landing of the membership funnel: consumes the token (soft
    // login) and continues into the purchase wizard — no quiz session.
    { path: 'join/start',        component: FunnelJoinStartPageComponent,  data: { animation: 'funnel-join-start' } },
    // DNA Family SPECIAL invitation landing (public — the invitee may have no
    // account yet; RouteService lists `family/join` as public).
    { path: 'family/join/:token', component: FamilyJoinPageComponent,      data: { animation: 'family-join' } },
    // Guardian consent link of a minor companion member (public — the parent is
    // no member; RouteService lists `companion/guardian` as public).
    { path: 'companion/guardian/:token', component: CompanionGuardianPageComponent, data: { animation: 'companion-guardian' } },
    // Retired Health Finder — routed so that flipping globalHealthFunnelMode back
    // to 'quiz' needs no route change. In membership mode nothing links here and
    // the backend answers quiz/start with 410.
    { path: 'quiz/start',        component: FunnelQuizStartPageComponent,  data: { animation: 'funnel-quiz-start' } },
    { path: 'quiz',              component: FunnelQuizPageComponent,       data: { animation: 'funnel-quiz' } },
    { path: 'quiz/result',       component: FunnelResultPageComponent,     data: { animation: 'funnel-result' } }
  )
  // .replace('main', {
  //   path: 'main',
  //   component: MainPageComponent,
  //   canActivate: [emailVerifiedGuard],
  //   data: { title: 'Dashboard', animation: 'main' }
  // })
  .protected(
    {
      path: 'membership/apply',
      component: MembershipApplyPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Membership', animation: 'membership-apply' }
    },
    // DNA Family SPECIAL: the main account's ten invitation slots. Visible in the
    // menu only with user.family_enabled; the backend answers 403/404 otherwise.
    {
      path: 'family',
      component: FamilyPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'My Family', animation: 'family' }
    },
    // "Meine Begleitung" — the daily companion (Api::HealthCompanionController).
    // Menu only with user.health_companion_enabled; the page explains every state.
    {
      path: 'companion',
      component: CompanionPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Meine Begleitung', animation: 'companion' }
    },
    { path: 'companion/:tab', component: CompanionPageComponent, canActivate: [emailVerifiedGuard],
      data: { title: 'Meine Begleitung', animation: 'companion' } },
    // Marketing Pool (docs/notes/MARKETING_POOL_SPEC.md §14): never public. The
    // guard reads user.marketing_pool (fail closed) and, for the holder of
    // existing investments whose visibility was revoked, asks the server.
    {
      path: 'marketing-pool',
      component: MarketingPoolPageComponent,
      canActivate: [emailVerifiedGuard, marketingPoolGuard],
      data: { title: 'Marketing Pool', animation: 'marketing-pool' }
    },
    // Health Tools — the two Zammad queues a member can talk to. Same component,
    // the Zammad group name and the i18n prefix come from the route data.
    {
      path: 'health-concierge',
      component: HealthToolsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: {
        title: 'Ask Dr. Future',
        animation: 'health-concierge',
        group: 'health_concierge',
        toolKey: 'health_tools.concierge'
      }
    },
    // Dr. Future — the AI health-information companion (Api::DrFutureController).
    // `?q=` opens a new conversation with that question (the dashboard hero).
    {
      path: 'dr-future',
      component: DrFuturePageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Ask Dr. Future', animation: 'dr-future', assistant: 'dr_future' }
    },
    // The DNA-based assistants (customer order 2026-10-09): the same page, one
    // assistant each. Visible to every member; the page shows the lock notice
    // while the DNA evaluation is not ready (the backend enforces it).
    ...DNA_ASSISTANTS.map(a => ({
      path: `dr-future/${a.slug}`,
      component: DrFuturePageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: a.name, animation: 'dr-future', assistant: a.key }
    })),
    // BodyIQ was called MoveIQ until 2026-10-09 (customer rename); the old slug stays.
    { path: 'dr-future/moveiq', redirectTo: 'dr-future/bodyiq' },
    // Unknown assistant → Dr. Future (redirectTo is relative to the :lang parent).
    { path: 'dr-future/:assistant', redirectTo: 'dr-future' },
    // Support hub (customer principle 5): one entry, three ways, the system routes.
    // The two target pages are the Health Tools page with a different Zammad group.
    {
      path: 'support',
      component: SupportHubPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Support', animation: 'support-hub' }
    },
    {
      path: 'support/request',
      component: HealthToolsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Support', animation: 'support-request', group: 'Support', toolKey: 'support_hub.general' }
    },
    {
      path: 'support/requests',
      component: HealthToolsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: {
        title: 'My requests', animation: 'support-requests',
        group: 'health_concierge', extraGroups: ['Support'], listOnly: true, toolKey: 'support_hub.requests'
      }
    },
    {
      path: 'ask-dr-kristy',
      component: HealthToolsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: {
        title: 'Ask Dr. Kristy',
        animation: 'ask-dr-kristy',
        group: 'ask_dr_kristy',
        toolKey: 'health_tools.kristy'
      }
    },
    {
      path: 'users',
      component: UsersPageComponent,
      data: { title: 'Users', animation: 'users' }
    },
    {
      path: 'user-statistics',
      component: UserStatisticsPageComponent,
      data: { title: 'User Statistics', animation: 'user-statistics' }
    },
    {
      path: 'earnings-statistics',
      component: EarningsStatisticsPageComponent,
      data: { title: 'Earnings Statistics', animation: 'earnings-statistics' }
    },
    {
      path: 'user-tree',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'user-tree/:userId',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'bookings',
      component: BookingsPageComponent,
      data: { title: 'Bookings', animation: 'bookings' }
    },
    // Appointments ("Termine") — the brand's published call schedule. Member
    // facing; the admin CRUD lives under /admin/appointments.
    {
      path: 'appointments',
      component: AppointmentsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Appointments', animation: 'appointments' }
    },
    {
      path: 'my-payouts',
      component: UserPayoutsPageComponent,
      data: { title: 'My Payouts', animation: 'user-payouts' }
    },
    {
      path: 'my-payouts/request',
      component: UserPayoutRequestPageComponent,
      data: { title: 'Request Payout', animation: 'user-payout-request' }
    },
    {
      path: 'delete-account',
      component: DeleteAccountPageComponent,
      data: { title: 'Delete Account', animation: 'delete-account' }
    },
    {
      path: 'enter-b-e-t-a-test',
      component: BetaActivationPageComponent,
      data: { title: 'Beta Program', animation: 'beta-activation' }
    },
    {
      path: 'kyc',
      component: KycFormPageComponent,
      canActivate: [kycFormGuard],
      data: { title: 'KYC Verification', animation: 'kyc' }
    },
    {
      path: 'kyc/confirmed',
      component: KycConfirmedPageComponent,
      data: { title: 'KYC Confirmed', animation: 'kyc-confirmed' }
    },
    {
      path: 'kyc/sumsub',
      component: KycSumsubPageComponent,
      data: { title: 'KYC Verification', animation: 'kyc-sumsub' }
    },
    {
      path: 'xshop-admin',
      component: XshopAdminPageComponent,
      data: { title: 'XShop Admin', animation: 'xshop-admin' }
    },
    // Marketing Pool admin — BEFORE the 'admin' prefix route on purpose: it is
    // opened by user.marketing_pool.can_manage (management and right holders need
    // not be platform admins), not by adminGuard. Lazy, like the other admin pages.
    {
      path: 'admin/marketing-pool',
      canActivate: [marketingPoolAdminGuard],
      loadComponent: () =>
        import('./admin/containers/admin-marketing-pool-page/admin-marketing-pool-page.component')
          .then(m => m.AdminMarketingPoolPageComponent),
      data: { title: 'Marketing Pool Admin', animation: 'admin-marketing-pool' }
    },
    {
      path: 'admin',
      canMatch: [adminGuard],
      canActivate: [adminGuard],
      loadChildren: () =>
        import('./admin/admin.routes')
          .then(routes => routes.ADMIN_ROUTES)
    }
  )
  .build();

  